/**
 * documentTopics — content index of an uploaded document (document-intelligence
 * plan P3: "categorise based on content, not file names").
 *
 * One upload can cover several subjects: a utility questionnaire is water AND
 * wastewater; an economic-development pack is water, flood and pipelines. Until
 * now routing (which SED sections a document is extracted into, which Coverage
 * Matrix requirements it can satisfy) came from the filename, the summary and
 * the first 4,000 characters. This pass reads the WHOLE body in windows and
 * asks the model for topical segments with:
 *
 *   - char offsets (absolute into the body) and the page they start on,
 *   - a short topic label and a summary that keeps the concrete figures,
 *   - the SED sections and checklist requirements the segment speaks to,
 *   - attribution when the text states it (who said it, in what role, when,
 *     and whether it was prepared for THIS project) — the seed for P4.
 *
 * Output lives on the document as `topicSegments` (GCS JSON / in session) and
 * the compact `topics` union (kept on the site record). Best-effort: a failed
 * or skipped pass leaves the document routed the old way.
 */
import type { SiteDocument, DocumentTopicSegment, DocumentTopics } from '../../types/siteRag';
import { SED_SECTION_IDS, type SedSectionId } from '../../types/siteEvidence';
import { checklistRequirementIds } from '../../config/documentChecklists';
import { documentBodyText, pageForCharOffset } from '../../lib/documentBody';
import { hasUsableExtractedText } from './extractedTextQuality';

export const TOPIC_SEGMENTER_VERSION = '2026.10.01-topics-v4-grounded-current';
/** Characters per model call. */
export const TOPIC_WINDOW_CHARS = 40_000;
/** Calls per document at most; longer bodies are sampled evenly (first and last windows always kept). */
export const TOPIC_MAX_WINDOWS = 50;
/** Below this the summary already covers the document. */
export const TOPIC_MIN_BODY_CHARS = 50;
export const TOPIC_MAX_SEGMENTS = 200;
const TOPIC_CALL_TIMEOUT_MS = 45_000;

export interface TopicWindow {
  start: number;
  end: number;
  text: string;
}

export interface RawTopicSegment {
  facts?: unknown;
  charStart?: unknown;
  charEnd?: unknown;
  topic?: unknown;
  summary?: unknown;
  sedSections?: unknown;
  requirementIds?: unknown;
  confidence?: unknown;
  statedBy?: unknown;
  statedByRole?: unknown;
  organisation?: unknown;
  statedOn?: unknown;
  appliesToProject?: unknown;
  projectScopeQuote?: unknown;
}

export interface RawTopicResponse {
  segments?: RawTopicSegment[];
}

/** Injectable model call (tests pass a stub). */
export type TopicSegmentRunner = (args: {
  contents: string;
  schema: Record<string, unknown>;
  signal?: AbortSignal;
}) => Promise<RawTopicResponse>;

/** Consecutive windows over the body; sampled evenly when the body is longer than `maxWindows` windows. */
export function buildTopicWindows(
  text: string,
  windowChars: number = TOPIC_WINDOW_CHARS,
  maxWindows: number = TOPIC_MAX_WINDOWS,
): TopicWindow[] {
  if (text.length === 0) return [];
  const all: TopicWindow[] = [];
  for (let start = 0; start < text.length; start += windowChars) {
    const end = Math.min(text.length, start + windowChars);
    all.push({ start, end, text: text.slice(start, end) });
  }
  if (all.length <= maxWindows) return all;
  // Even sampling that always keeps the first and the last window.
  const picked = new Set<number>([0, all.length - 1]);
  const step = (all.length - 1) / Math.max(1, maxWindows - 1);
  for (let i = 1; i < maxWindows - 1; i++) picked.add(Math.round(i * step));
  return [...picked].sort((a, b) => a - b).map((i) => all[i]!);
}

function str(v: unknown, max: number): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t.slice(0, max) : null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function ids(v: unknown, known: ReadonlySet<string>): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const item of v) {
    if (typeof item !== 'string') continue;
    const t = item.trim();
    if (known.has(t) && !out.includes(t)) out.push(t);
  }
  return out;
}

const APPLIES = new Set(['this-site', 'other-project', 'unknown']);

type ProjectScope = NonNullable<DocumentTopicSegment['attribution']>['appliesToProject'];

// These are source statements about scope, not scenario IDs or metadata labels.
// A fictional/current declaration may explain a not-real-record disclaimer; it
// cannot convert an estate capacity or a genuinely different project to local.
const FICTIONAL_CURRENT_SCOPE = /\b(?:they|(?:all|these|the)\s+(?:findings|project values|design facts|allocation and demand values|inputs|assumptions))\s+(?:describe|apply to|address)\s+(?:only\s+)?(?:this|the)\s+fictional\s+(?:(?:test|development)\s+)?(?:copy|project|case|scenario|parcel)\b/i;
const DIFFERENT_PROJECT_SCOPE = /\b(?:prepared|written|issued|provided|supplied|was|were|is|are)\s+(?:specifically\s+)?for\s+(?:an?\s+)?(?:another|different|other)\s+(?:project|site|parcel)\b/gi;
const CONTEXT_CAPACITY_SCOPE = /\b(?:estate|utility|park|system)[ -]wide\s+(?:capacity|supply|figures|data)|\bestate context only\b|\b(?:neither is|not)\s+(?:an?\s+)?project allocation\b/i;

function fictionalCurrentStatement(
  window: TopicWindow,
  withinStart = 0,
  withinEnd = window.text.length,
): RegExpExecArray | undefined {
  const text = window.text;
  for (const match of text.matchAll(new RegExp(FICTIONAL_CURRENT_SCOPE.source, 'gi'))) {
    const end = match.index + match[0].length;
    if (match.index < withinStart || end > withinEnd) continue;
    const before = text.slice(0, match.index);
    const clauseStart = Math.max(...['.', '!', '?'].map(mark => before.lastIndexOf(mark))) + 1;
    // The complete source clause must affirm scope directly. Do not validate
    // only a model-selected substring of "It is not true that all inputs ...".
    // PDF line wraps and semicolons do not establish a new affirmative sentence.
    // A non-first window may start partway through such a clause.
    if ((clauseStart === 0 && window.start > 0) || text.slice(clauseStart, match.index).trim()) continue;
    const after = text.slice(end);
    const delimiter = after.search(/[.!?]/);
    const tail = delimiter < 0 ? after : after.slice(0, delimiter);
    if (delimiter >= 0 && after[delimiter] === '?') continue;
    // The source's affirmative fictional-project declaration may explicitly
    // disclaim the real record, as RR-F03 does. Other negation or uncertainty
    // in the same clause cannot establish current-project applicability.
    if (!/^(?:only|,?\s*not to the real (?:park or tract|site|parcel|record|project))?$/i.test(tail.trim())) continue;
    return match;
  }
  return undefined;
}

function differentProjectStatement(
  window: TopicWindow,
  withinStart: number,
  withinEnd: number,
): { scope: ProjectScope; evidence: NonNullable<DocumentTopicSegment['projectScopeEvidence']> } | undefined {
  const notCurrent = /\b(?:do|does) not (?:address|apply to|describe|concern) this (?:project|site|parcel)\b|\bnot (?:for|about) this (?:project|site|parcel)\b/;
  const candidates = new RegExp(`${DIFFERENT_PROJECT_SCOPE.source}|${notCurrent.source}`, 'gi');
  for (const match of window.text.matchAll(candidates)) {
    const end = match.index + match[0].length;
    if (match.index < withinStart || end > withinEnd) continue;
    const before = window.text.slice(0, match.index);
    const sentenceStart = Math.max(...['.', '!', '?'].map(mark => before.lastIndexOf(mark))) + 1;
    const after = window.text.slice(end);
    const delimiter = after.search(/[.!?]/);
    const sentenceEnd = delimiter < 0 ? window.text.length : end + delimiter + 1;
    const prefix = window.text.slice(sentenceStart, match.index).trim().replace(/\s+/g, ' ');
    const tail = (delimiter < 0 ? after : after.slice(0, delimiter)).trim();
    // Only direct source assertions establish foreign applicability. A model
    // cannot hide "It is not true that ..." in a quote or a segment boundary.
    // More complicated named or conditional assertions retain unknown scope.
    const subject = '(?:(?:this|these|those|the|our|their)(?: (?:utility|municipal|water|power|supply|source|prior|earlier|original|technical|document|questionnaire|study|answers|values|figures|data|capacity|report|statements|measurements))*)';
    const affirmativePrefix = new RegExp(`^(?:${subject}(?: (?:was|were|is|are))?)?$`, 'i').test(prefix)
      || new RegExp(`^${subject} belongs? to [A-Za-z0-9 -]+ and$`, 'i').test(prefix);
    const affirmative = affirmativePrefix && /^(?:only)?$/i.test(tail)
      && !(delimiter >= 0 && after[delimiter] === '?')
      && !(sentenceStart === 0 && window.start > 0);
    const full = window.text.slice(sentenceStart, sentenceEnd);
    const leading = full.length - full.trimStart().length;
    const sourceQuote = full.trim();
    return { scope: affirmative ? 'other-project' : 'unknown', evidence: {
      sourceQuote, charStart: window.start + sentenceStart + leading,
      charEnd: window.start + sentenceStart + leading + sourceQuote.length,
    } };
  }
  return undefined;
}

function quotedProjectScope(
  seg: RawTopicSegment,
  window: TopicWindow,
  relStart: number,
  relEnd: number,
): { scope: ProjectScope; evidence?: DocumentTopicSegment['projectScopeEvidence'] } {
  const raw = str(seg.appliesToProject, 20)?.toLowerCase().replace(/\s+/g, '-');
  const scope: ProjectScope = raw && APPLIES.has(raw) ? raw as ProjectScope : 'unknown';
  const quote = str(seg.projectScopeQuote, 600);
  const quoteOffset = quote ? window.text.indexOf(quote) : -1;
  const local = window.text.slice(relStart, relEnd);
  const localOther = differentProjectStatement(window, relStart, relEnd);
  const localContext = CONTEXT_CAPACITY_SCOPE.exec(local);
  const currentDeclaration = fictionalCurrentStatement(window);
  const literalQuote = quote && quoteOffset >= 0 ? quote : undefined;
  const quoteIsLocal = quoteOffset >= relStart && quoteOffset + (literalQuote?.length ?? 0) <= relEnd;
  const evidence = (text: string, offset: number): NonNullable<DocumentTopicSegment['projectScopeEvidence']> => ({
    sourceQuote: text, charStart: window.start + offset, charEnd: window.start + offset + text.length,
  });

  // Local source qualifications win over a document-level current-project
  // declaration. Mixed local/estate passages remain context until typed facts
  // are extracted with their individual source scope.
  const quotedOther = literalQuote && quoteIsLocal
    ? differentProjectStatement(window, quoteOffset, quoteOffset + literalQuote.length) : undefined;
  if (localOther || quotedOther) {
    return localOther ?? quotedOther!;
  }
  if (localContext || (literalQuote && CONTEXT_CAPACITY_SCOPE.test(literalQuote))) {
    return { scope: 'unknown', evidence: localContext
      ? evidence(localContext[0], relStart + localContext.index) : evidence(literalQuote!, quoteOffset) };
  }
  if (literalQuote && quoteIsLocal && fictionalCurrentStatement(window, quoteOffset, quoteOffset + literalQuote.length)) {
    return { scope: 'this-site', evidence: evidence(literalQuote, quoteOffset) };
  }
  if (scope === 'other-project' && literalQuote && quoteIsLocal) {
    // Preserve a source-quoted foreign qualification even when its named-project
    // phrasing is outside the small grammar above. A general cover disclaimer
    // cannot overrule this segment's own source scope.
    return { scope, evidence: evidence(literalQuote, quoteOffset) };
  }
  if (scope === 'other-project' && currentDeclaration) {
    // An unsupported model label must not make the fictional project foreign
    // solely because its source disclaims claims about the real record. Lack
    // of a valid scope quote stays unknown rather than asserting applicability.
    return { scope: 'unknown', evidence: evidence(currentDeclaration[0], currentDeclaration.index) };
  }
  if (scope === 'this-site') {
    // A model's current-project label is not source proof. Only the earlier
    // literal, local, affirmative full-sentence qualification may establish it.
    // Preserve a local quote for review, including uncertainty, without turning
    // a quantity, disclaimer, missing quote or clipped context into applicability.
    return { scope: 'unknown', ...(literalQuote && quoteIsLocal ? { evidence: evidence(literalQuote, quoteOffset) } : {}) };
  }
  return { scope, ...(literalQuote && quoteIsLocal ? { evidence: evidence(literalQuote, quoteOffset) } : {}) };
}

/** Validate one window's raw segments into absolute, typed segments. Pure. */
export function normaliseTopicSegments(
  raw: RawTopicResponse | null | undefined,
  window: TopicWindow,
  doc: Pick<SiteDocument, 'id' | 'pages'>,
  knownSections: ReadonlySet<string> = new Set(SED_SECTION_IDS),
  knownRequirements: ReadonlySet<string> = new Set(checklistRequirementIds()),
): DocumentTopicSegment[] {
  const out: DocumentTopicSegment[] = [];
  const windowLen = window.end - window.start;
  for (const seg of raw?.segments ?? []) {
    const relStart = Math.max(0, Math.min(windowLen, Math.floor(num(seg.charStart) ?? 0)));
    const relEndRaw = num(seg.charEnd);
    const relEnd = Math.max(relStart, Math.min(windowLen, Math.floor(relEndRaw ?? windowLen)));
    if (relEnd <= relStart) continue;
    const topic = str(seg.topic, 80);
    const summary = str(seg.summary, 320);
    if (!topic && !summary) continue;
    const charStart = window.start + relStart;
    const charEnd = window.start + relEnd;
    const projectScope = quotedProjectScope(seg, window, relStart, relEnd);
    const attribution = {
      statedBy: str(seg.statedBy, 120),
      statedByRole: str(seg.statedByRole, 120),
      organisation: str(seg.organisation, 120),
      statedOn: str(seg.statedOn, 40),
      appliesToProject: projectScope.scope,
    };
    const hasAttribution = attribution.statedBy || attribution.statedByRole || attribution.organisation
      || attribution.statedOn || attribution.appliesToProject !== 'unknown';
    const facts: NonNullable<DocumentTopicSegment['facts']> = [];
    for (const rawFact of Array.isArray(seg.facts) ? seg.facts.slice(0, 40) : []) {
      if (!rawFact || typeof rawFact !== 'object') continue;
      const fact = rawFact as Record<string, unknown>;
      const label = str(fact.label, 100);
      const value = str(fact.value, 160);
      const sourceQuote = str(fact.sourceQuote, 400);
      if (!label || !value || !sourceQuote) continue;
      const offset = window.text.indexOf(sourceQuote);
      // Require a literal source quote and a stated value. Never turn a generated
      // summary or calculation into a fact the document did not contain.
      const normal = (s: string) => s.replace(/[,\s]/g, '').toLowerCase();
      if (offset < 0 || !normal(sourceQuote).includes(normal(value))) continue;
      const unit = str(fact.unit, 50);
      facts.push({ label, value, ...(unit ? { unit } : {}), sourceQuote, charStart: window.start + offset, charEnd: window.start + offset + sourceQuote.length });
    }
    out.push({
      id: `${doc.id}-s${charStart}`,
      charStart,
      charEnd,
      ...(pageForCharOffset(doc.pages, charStart) !== undefined ? { page: pageForCharOffset(doc.pages, charStart) } : {}),
      topic: topic ?? summary!.slice(0, 80),
      summary: summary ?? topic!,
      sedSections: ids(seg.sedSections, knownSections) as SedSectionId[],
      requirementIds: ids(seg.requirementIds, knownRequirements),
      confidence: Math.max(0, Math.min(1, num(seg.confidence) ?? 0.5)),
      ...(facts.length ? { facts } : {}),
      ...(projectScope.evidence ? { projectScopeEvidence: projectScope.evidence } : {}),
      ...(hasAttribution ? { attribution } : {}),
    });
  }
  return out.sort((a, b) => a.charStart - b.charStart);
}

/** Compact union for the site record and routing. Pure. */
export function compactTopics(segments: readonly DocumentTopicSegment[], segmentedAt: string): DocumentTopics {
  const sectionCounts = new Map<SedSectionId, number>();
  const requirementCounts = new Map<string, number>();
  const topics: string[] = [];
  for (const s of segments) {
    for (const sec of s.sedSections) sectionCounts.set(sec, (sectionCounts.get(sec) ?? 0) + 1);
    for (const req of s.requirementIds) requirementCounts.set(req, (requirementCounts.get(req) ?? 0) + 1);
    if (topics.length < 12 && !topics.some((t) => t.toLowerCase() === s.topic.toLowerCase())) topics.push(s.topic);
  }
  const byCount = <K>(m: Map<K, number>): K[] => [...m.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  return {
    sedSections: byCount(sectionCounts),
    requirementIds: byCount(requirementCounts),
    topics,
    segmentCount: segments.length,
    segmentedAt,
    version: TOPIC_SEGMENTER_VERSION,
  };
}

function buildPrompt(doc: Pick<SiteDocument, 'fileName' | 'summary'>, window: TopicWindow, bodyLength: number): string {
  return [
    'You are indexing a document uploaded for a data-centre site evaluation. Split the TEXT WINDOW below into',
    'topical segments so each subject the document covers can be routed to the right evidence slot.',
    '',
    `DOCUMENT FILENAME: ${doc.fileName}`,
    `DOCUMENT SUMMARY: ${(doc.summary ?? '').slice(0, 400)}`,
    `WINDOW: characters ${window.start}-${window.end} of ${bodyLength}`,
    '',
    'For every segment return:',
    '  - charStart / charEnd: offsets RELATIVE TO THE START OF THIS WINDOW (0-based). Segments must not overlap and',
    '    together should cover the window; skip boilerplate (headers, footers, signatures) by leaving gaps.',
    '  - topic: ≤ 60 characters naming the subject (e.g. "Municipal wastewater capacity").',
    '  - summary: ≤ 240 characters that KEEP the concrete figures, units, dates and names in the text.',
    '  - facts: up to 40 source-stated facts per segment, each {label, value, unit?, sourceQuote}. Keep original value text and units; quote a short verbatim source sentence containing the value.',
    '    Include power/generation capacities, energy demand, tariffs, fuel conditions, equipment quantities, costs, finance terms, water/wastewater, dates, distances and constraints when stated.',
    '    For questionnaires/forms distinguish printed questions and column headings from filled answers. A blank answer is missing evidence: never use another field heading, an unchecked option or an unanswered prompt as the value. Keep cell/row references in the source quotation when available.',
    '    A fact is evidence, not an adopted design choice. Never invent conversion factors, financial assumptions, approval or capacity commitments. Preserve survey and other-project scope.',
    `  - sedSections: any of [${SED_SECTION_IDS.join(', ')}] the segment gives evidence for (empty when none).`,
    `  - requirementIds: any of [${checklistRequirementIds().join(', ')}] the segment could satisfy (empty when none).`,
    '  - confidence: 0..1 that the segment really carries evidence for those sections/requirements.',
    '  - attribution, ONLY when the text states it: statedBy (person or body), statedByRole, organisation, statedOn',
    "    (date as written), appliesToProject: 'this-site' | 'other-project' | 'unknown' — use 'other-project' when the",
    '    text says the answers were prepared for a different project, and never guess.',
    '    projectScopeQuote: a verbatim source quotation supporting the stated project scope. A disclaimer that fictional test-copy values are not real-world findings does not make them another project. Where the text explicitly describes this fictional test copy, preserve that stated scope without claiming real verification or approval.',
    '    Current-project applicability requires an affirmative project-scope quotation within this segment; a quantity alone, a missing/nonliteral/nonlocal quote, or conditional/uncertain scope must remain unknown even if the document is uploaded for this site.',
    '    Keep genuinely different-project statements and estate/utility-wide capacities as context. Estate capacity is not a project allocation. A scenario ID or synthetic label alone does not establish project applicability. If the source does not establish scope, use unknown.',
    '    A printed form template, cited regulation or agency footer is not evidence that the agency authored, approved or certified the filled answers. Preserve the named investigator/report author when explicitly stated.',
    '',
    'Return ONLY JSON: { "segments": [ ... ] }',
    '',
    'TEXT WINDOW:',
    window.text,
  ].join('\n');
}

function responseSchema(): Record<string, unknown> {
  // Built lazily so the module does not import the Gemini client at load time.
  return {
    type: 'OBJECT',
    properties: {
      segments: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          properties: {
            charStart: { type: 'NUMBER' },
            charEnd: { type: 'NUMBER' },
            topic: { type: 'STRING' },
            summary: { type: 'STRING' },
            sedSections: { type: 'ARRAY', items: { type: 'STRING' } },
            requirementIds: { type: 'ARRAY', items: { type: 'STRING' } },
            confidence: { type: 'NUMBER' },
            facts: { type: 'ARRAY', items: { type: 'OBJECT', properties: {
              label: { type: 'STRING' }, value: { type: 'STRING' }, unit: { type: 'STRING' }, sourceQuote: { type: 'STRING' },
            }, required: ['label', 'value', 'sourceQuote'] } },
            statedBy: { type: 'STRING' },
            statedByRole: { type: 'STRING' },
            organisation: { type: 'STRING' },
            statedOn: { type: 'STRING' },
            appliesToProject: { type: 'STRING' },
            projectScopeQuote: { type: 'STRING' },
          },
          required: ['charStart', 'charEnd', 'topic', 'summary', 'sedSections', 'requirementIds', 'confidence', 'facts'],
        },
      },
    },
    required: ['segments'],
  };
}

async function defaultRunner(args: { contents: string; schema: Record<string, unknown>; signal?: AbortSignal }): Promise<RawTopicResponse> {
  const { runGeminiJSON } = await import('../geminiClient');
  const { tieredModelFor } = await import('../../config/modelTiering');
  return runGeminiJSON<RawTopicResponse>({
    contents: args.contents,
    schema: args.schema as never,
    model: tieredModelFor('extraction'),
    timeoutMs: TOPIC_CALL_TIMEOUT_MS,
    signal: args.signal,
  });
}

export interface SegmentDocumentTopicsOptions {
  runner?: TopicSegmentRunner;
  maxWindows?: number;
  abortSignal?: AbortSignal;
  now?: () => string;
  /** Defaults true; false discards prior window receipts for a deliberate full refresh. */
  resumeFailedWindows?: boolean;
  /** Bounded window concurrency (default 3) so multi-window docs don't stall for 8 × 25s serially. */
  windowConcurrency?: number;
  /** Optional overall timeout in ms for the entire topic-indexing pass. */
  timeoutMs?: number;
}

export async function topicBodyHash(body: string): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(body));
  return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Index a document's body. Returns null when the document is too short or
 * carries no usable text, or when every window failed. Never throws.
 */
export async function segmentDocumentTopics(
  doc: SiteDocument,
  opts: SegmentDocumentTopicsOptions = {},
): Promise<{ segments: DocumentTopicSegment[]; topics: DocumentTopics } | null> {
  const body = documentBodyText(doc);
  if (!hasUsableExtractedText(body) || body.length < TOPIC_MIN_BODY_CHARS) return null;
  const runner = opts.runner ?? defaultRunner;
  const allWindows = buildTopicWindows(body, TOPIC_WINDOW_CHARS, opts.maxWindows ?? TOPIC_MAX_WINDOWS);
  const bodyHash = await topicBodyHash(body);
  const previous = doc.topics?.coverage;
  const canResume = opts.resumeFailedWindows !== false && doc.topics?.version === TOPIC_SEGMENTER_VERSION
    && previous?.bodyHash === bodyHash && previous.windows.some(w => w.status === 'failed')
    && (doc.topicSegments?.length ?? 0) > 0;
  const completed = canResume ? previous.windows.filter(w => w.status === 'complete') : [];
  const windows = allWindows.filter(w => !completed.some(old => old.start === w.start && old.end === w.end));
  const receipts: NonNullable<DocumentTopics['coverage']>['windows'] = allWindows.map(w => ({
    start: w.start, end: w.end,
    status: completed.some(old => old.start === w.start && old.end === w.end) ? 'complete' : 'failed',
  }));
  const schema = responseSchema();
  const knownSections = new Set<string>(SED_SECTION_IDS);
  const knownRequirements = new Set<string>(checklistRequirementIds());
  const segments: DocumentTopicSegment[] = canResume
    ? (doc.topicSegments ?? []).filter(s => completed.some(w => s.charStart >= w.start && s.charStart < w.end)) : [];
  let windowsOk = completed.length;

  const passController = new AbortController();
  const onParentAbort = () => passController.abort(opts.abortSignal?.reason);
  if (opts.abortSignal) {
    if (opts.abortSignal.aborted) {
      passController.abort(opts.abortSignal.reason);
    } else {
      opts.abortSignal.addEventListener('abort', onParentAbort, { once: true });
    }
  }
  const passTimer =
    typeof opts.timeoutMs === 'number' && opts.timeoutMs > 0
      ? setTimeout(() => passController.abort(new Error('topic index timeout')), opts.timeoutMs)
      : undefined;

  try {
    const concurrency = Math.max(1, Math.min(windows.length || 1, opts.windowConcurrency ?? 3));
    let cursor = 0;
    const worker = async (): Promise<void> => {
      while (cursor < windows.length) {
        if (passController.signal.aborted) break;
        const idx = cursor++;
        const window = windows[idx]!;
        try {
          const raw = await runner({
            contents: buildPrompt(doc, window, body.length),
            schema,
            signal: passController.signal,
          });
          segments.push(...normaliseTopicSegments(raw, window, doc, knownSections, knownRequirements));
          windowsOk += 1;
          receipts.find(w => w.start === window.start && w.end === window.end)!.status = 'complete';
        } catch (e) {
          console.warn(`[documentTopics] window ${window.start}-${window.end} failed for ${doc.fileName}:`, (e as Error)?.message);
        }
      }
    };
    await Promise.all(Array.from({ length: concurrency }, () => worker()));
  } finally {
    if (passTimer) clearTimeout(passTimer);
    if (opts.abortSignal) opts.abortSignal.removeEventListener('abort', onParentAbort);
  }
  if (windowsOk === 0 || segments.length === 0) return null;
  const kept = segments.sort((a, b) => a.charStart - b.charStart).slice(0, TOPIC_MAX_SEGMENTS);
  const segmentedAt = (opts.now ?? (() => new Date().toISOString()))();
  return { segments: kept, topics: { ...compactTopics(kept, segmentedAt), coverage: {
    bodyHash, windows: receipts, ...(segments.length > kept.length ? { segmentsTruncated: true } : {}),
  } } };
}

/** Requirement ids a document's content index names (compact union or segments). */
export function documentContentRequirementIds(doc: Pick<SiteDocument, 'topics' | 'topicSegments'>): string[] {
  const out = new Set<string>(doc.topics?.requirementIds ?? []);
  for (const s of doc.topicSegments ?? []) for (const r of s.requirementIds) out.add(r);
  return [...out];
}

/** True when the document's content index names the requirement. */
export function documentContentNamesRequirement(doc: Pick<SiteDocument, 'topics' | 'topicSegments'>, requirementId: string): boolean {
  return documentContentRequirementIds(doc).includes(requirementId);
}
